import { type ReactNode, useRef } from 'react'
import { parseAsStringEnum, useQueryState } from 'nuqs'
import { useRequiredParams } from '@util'
import { isNotDefined, type WithConversationId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { Card, Stack, Tabs } from '@mantine/core'
import { useQueryDisclosure } from '@hooks'
import { formatDate, SizePrimitives, useConversation, useTranslation } from '@front/model'
import {
  AssignConversationDrawer,
  ConversationAudioPlayer,
  ConversationInsights,
  ConversationTranscript,
  NotReady,
  Page,
  TButton,
} from '@components'

const TAB_VALUES = ['insights', 'transcript'] as const
type TabValue = (typeof TAB_VALUES)[number]

function ConversationDetailRoute(): ReactNode {
  const { convoId } = useRequiredParams<WithConversationId>()
  const { t } = useTranslation()
  const [isAssignDrawerOpen, { open: openAssignDrawer, close: closeAssignDrawer }] = useQueryDisclosure('assign')
  const audioRef = useRef<HTMLAudioElement>(null)
  const [tab, setTab] = useQueryState('tab', parseAsStringEnum<TabValue>([...TAB_VALUES]).withDefault('insights'))
  const setAudioPosition = (position: number): void => {
    if (audioRef.current) {
      audioRef.current.currentTime = position
    }
  }

  const { data: conversation, isLoading, error } = useConversation(convoId)

  if (isLoading || isNotDefined(conversation)) {
    return <NotReady isLoading={isLoading} error={error ?? t('Conversation not found')} id='Conversation' />
  }

  return (
    <Page>
      <Page.Heading title={formatDate(conversation.convoDate, { format: 'MMM d, yyyy h:mm a' })} back>
        {!conversation.assigneeId && (
          <TButton icon='how_to_reg' variant='secondary' onClick={openAssignDrawer}>
            {t('Assign for Review')}
          </TButton>
        )}
      </Page.Heading>

      <Page.FunctionBar grow>
        <Card withBorder>
          <ConversationAudioPlayer conversation={conversation} audioRef={audioRef} />
        </Card>
      </Page.FunctionBar>

      <Page.Content className='overflow-y-auto'>
        <Stack gap={SizePrimitives['4']}>
          <Tabs
            value={tab}
            onChange={(value) => {
              if (value === 'insights' || value === 'transcript') {
                setTab(value)
              }
            }}
          >
            <Tabs.List>
              <Tabs.Tab value='insights'>Insights</Tabs.Tab>
              <Tabs.Tab value='transcript'>Transcript</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value='insights' pt={SizePrimitives['4']}>
              <ConversationInsights conversation={conversation} />
            </Tabs.Panel>

            <Tabs.Panel value='transcript' pt={SizePrimitives['4']}>
              <ConversationTranscript
                conversation={conversation}
                audioRef={audioRef}
                onSeek={setAudioPosition}
                isVisible={tab === 'transcript'}
              />
            </Tabs.Panel>
          </Tabs>
        </Stack>
        <AssignConversationDrawer conversation={conversation} opened={isAssignDrawerOpen} onClose={closeAssignDrawer} />
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/conversations_/$convoId/')({
  component: ConversationDetailRoute,
})
